import Loading from '@/components/Loading';
import NoData from '@/components/NoData';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import Filters from '@/components/UI/Filters';
import SearchFilterBar from '@/components/UI/SearchFilterBar';
import TableFilterMenu from '@/components/UI/SelectDropDown/Menus/TableFilterMenu';
import WorkflowAlerts from '@/components/Workflow/Show/WorkflowAlerts';
import { animalIdsRecord } from '@/constants/utils';
import { infoToast, removeParamsFromUrl, trunc } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty, _noop, _notNil } from '@/littledash';
import type { AnimalIdentifier } from '@/model/Animal.model';
import type { Cage } from '@/model/Cage.model';
import type { Study } from '@/model/Study.model';
import { ApiService } from '@/support/ApiService';
import * as Auth from '@/support/auth';
import { useFetchCollection, useFetchEntity } from '@/support/Hooks/fetch';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { isClosed } from '@/support/study';
import { DateUtils } from '@/utils/Date.utils';
import { type FC, type ReactNode, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { Dispatch } from 'redux';
import AnimalSelectPanel from './AnimalSelectPanel';
import EmptyWorkflow from './EmptyWorkflow';
import { assembleWorkflowFilterOptions, generateFilterParams, reducer, unresolvedAlerts } from './Show.utils';
import type { ShowState, ShowStateReducer, WorkflowTabConfig } from './Workflow.model';
import './Workflow.scss';
import WorkflowStage from './WorkflowStage';
import { type WorkflowShowProps } from './WorkflowTemplate.utils';
import { type TaskApiId } from '@/model/Task.model.ts';
import { RiCalendarLine } from 'react-icons/ri';
import Tooltip from '@/components/UI/Tooltip';
import {
  useUpdateSubjectActions,
  useSearchQuery,
  useSelectedAnimal,
  useSelectedCage,
  useWorkflowSetup,
  useSubjects,
  useAlerts,
  useSavedValueDifference,
} from '@/utils/workflow/useWorkflow.tsx';
import WorkflowConsumption from '@/components/Workflow/Show/WorkflowConsumption.tsx';

const Show: FC<WorkflowShowProps> = ({ templateDefaults }) => {
  const location = useLocation();
  const { id: studyId } = useParams();
  const [filterParams, setFilterParams] = useState<string>('');
  const [taskApiIds, setTaskApiIds] = useState<Array<TaskApiId>>(
    () => new URLSearchParams(location.search).getAll('taskApiId') as Array<TaskApiId>
  );
  const [subjectsLoading, setSubjectsLoading] = useState<boolean>(true);
  const { originalSubjects } = useSubjects();
  const { updateOriginalSubjects } = useUpdateSubjectActions();
  const { animalAlerts, updateAlerts } = useAlerts();
  const { clearAllSavedValueDifference } = useSavedValueDifference();

  const initialState: ShowState = useMemo(
    () => ({
      filters: [],
      collections: [],
      filterMatch: true,
      checklist: {},
      filterOptions: [],
    }),
    []
  );

  const { entity: study, entityLoading: studyLoading } = useFetchEntity<Study>({
    entityType: 'study',
    params: { id: studyId },
    includes: 'users,metadata,study_groups',
  });
  const { collection: collections, collectionLoading: collectionsLoading } = useFetchCollection<Cage>({
    collectionType: 'studyCages',
    params: { studyId },
    includes: 'animals_count',
    queryParams: { perPage: 1000, sort: 'cage_order', order: 'asc' },
  });

  // Sort by cage_order to ensure next_animal increments correctly
  const fetchSubjects = async () => {
    if (_notNil(study) && _notNil(study?.api_id)) {
      const response = await ApiService.call({
        endpoint: 'GET /api/v1/studies/{studyId}/animals',
        path: { studyId: study.api_id },
        query: {
          include: ['alerts', 'terminated_at_data', 'metadata'],
          perPage: 1000,
          sort: 'cage_order',
          order: 'asc',
          ...(_isNotEmpty(taskApiIds) && { task_ids: [...taskApiIds] }),
        },
        filters: filterParams.replaceAll('[]', '%5B%5D').replaceAll('|', '%7C'),
      });
      if (response.type === 'success') {
        // @ts-expect-error: OAI animal does not match manually created animal
        updateOriginalSubjects(response.body.data);
      }
      setSubjectsLoading(false);
    }
  };

  useEffect(() => {
    if (study || filterParams) {
      fetchSubjects();
    }
  }, [study, filterParams, taskApiIds]);

  const hasAccess = study && Auth.isWriteUserForStudy(study);

  const [state, dispatch] = useReducer<ShowStateReducer>(reducer, initialState);
  const searchBarRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (_notNil(originalSubjects)) {
      updateAlerts({ type: 'CLEAR_ALERTS' });
      const originalAlerts = originalSubjects.flatMap((subject) =>
        _isNil(subject?.terminated_at) ? (subject?.alerts ?? []) : []
      );
      if (_isNotEmpty(originalAlerts)) {
        updateAlerts({ type: 'ADD_ALERTS', newAlerts: originalAlerts });
      }
    }
  }, [originalSubjects]);

  useEffect(() => {
    if (_notNil(collections)) {
      dispatch({ type: 'setCollections', data: collections });
    }
  }, [collections]);

  useEffect(() => {
    if (study && collections) {
      const filterOptions = assembleWorkflowFilterOptions({
        study,
        collections,
      });
      dispatch({ type: 'setFilterOptions', data: filterOptions });
    }
  }, [study, collections]);

  const { filterOptions, filterMatch, filters } = state;
  const { selectedAnimal } = useSelectedAnimal();
  const { selectedCage } = useSelectedCage();
  const { handleSearchQuery, searchQuery } = useSearchQuery();

  const { workflowSetup, updateWorkflowSetup } = useWorkflowSetup();

  // Track previous measured_at to detect date changes
  const prevMeasuredAtRef = useRef(workflowSetup?.measured_at);

  const workflow: Array<WorkflowTabConfig> = useMemo(
    () => workflowSetup?.workflowTabConfig ?? ([] as Array<WorkflowTabConfig>),
    [workflowSetup?.workflowTabConfig]
  );
  const idToSearch: AnimalIdentifier = useMemo(
    () => workflowSetup?.idToSearch ?? ('name' as AnimalIdentifier),
    [workflowSetup?.idToSearch]
  );
  const displayFoodConsumption: boolean = useMemo(
    () =>
      ((workflowSetup?.foodIntakeEnabled || workflowSetup?.fluidIntakeEnabled) ?? false) &&
      _isNil(selectedAnimal) &&
      _notNil(selectedCage) &&
      !collectionsLoading,
    [
      workflowSetup?.foodIntakeEnabled,
      workflowSetup?.fluidIntakeEnabled,
      selectedAnimal,
      selectedCage,
      collectionsLoading,
    ]
  );
  useEffect(() => {
    updateWorkflowSetup({
      idToSearch: templateDefaults?.idToSearch ?? 'name',
      idToDisplay: templateDefaults?.idToDisplay ?? 'name',
      measured_at: templateDefaults?.measured_at ?? DateUtils.dateNow(),
      showGroups: templateDefaults?.showTreatmentGroups ?? false,
      startTab: templateDefaults?.startOn ?? 'measurements',
      playSound: templateDefaults?.playSound ?? true,
      dosingData: workflowSetup?.dosingData ?? [],
      foodIntakeEnabled: workflowSetup?.foodIntakeEnabled ?? false,
      fluidIntakeEnabled: workflowSetup?.fluidIntakeEnabled ?? false,
      intakeTimeEnabled: workflowSetup?.intakeTimeEnabled ?? false,
      workflowTabConfig: workflowSetup?.workflowTabConfig ?? [],
    });
  }, [templateDefaults]);

  // Clear all saved percentages when measured_at changes
  useEffect(() => {
    const prevMeasuredAt = prevMeasuredAtRef.current;
    const currentMeasuredAt = workflowSetup?.measured_at;

    // Only clear when both previous and current are non-null/non-empty AND different
    if (prevMeasuredAt && currentMeasuredAt && prevMeasuredAt !== currentMeasuredAt) {
      clearAllSavedValueDifference();
    }

    // Update ref with current value
    prevMeasuredAtRef.current = currentMeasuredAt;
  }, [workflowSetup?.measured_at, clearAllSavedValueDifference]);

  const storeDispatch = useDispatch();
  const openModal = (modal: string, props: unknown) => {
    storeDispatch({
      type: 'OPEN_MODAL',
      modal,
      props,
    });
  };

  const openFlash = (res: ReactNode) => {
    toast.success(res);
  };

  const confirmUnarchive = (study: Study, storeDispatch: Dispatch) => {
    Http.post(apiRoute('studies.unarchive', { id: study.id }))
      .then(({ data }) => {
        storeDispatch({ type: 'CLOSE_MODAL' });
        toast.success(`Successfully ${study.archived_at ? 'resumed' : 'completed'} ${study.name}`);
        setupWorkflow();
        storeDispatch({
          type: 'SET_CURRENT_STUDY',
          study: data.data,
        });
      })
      .catch(_noop);
  };

  const confirmUncancel = (study: Study, storeDispatch: Dispatch) => {
    Http.post(apiRoute('studies.uncancel', { id: study.id }))
      .then(({ data }) => {
        storeDispatch({ type: 'CLOSE_MODAL' });
        toast.success(`Successfully ${study.canceled_at ? 'resumed' : 'cancelled'} ${study.name}`);
        setupWorkflow();
        storeDispatch({
          type: 'SET_CURRENT_STUDY',
          study: data.data,
        });
      })
      .catch(_noop);
  };

  const focusSearch = () => {
    if (_notNil(searchBarRef.current)) {
      searchBarRef.current?.focus();
      searchBarRef.current?.select();
      searchBarRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const setupWorkflow = () => {
    if (hasAccess) {
      openModal('SETUP_WORKFLOW', {
        study,
        workflowSetup,
        updateWorkflowSetup,
        templateDefaults,
        handleCallback: () =>
          window.setTimeout(() => {
            storeDispatch({ type: 'CLOSE_MODAL' });
            if (focusSearch) {
              focusSearch();
            }
          }, 100),
      });
    }
  };

  useEffect(() => {
    if (!studyLoading && study && !isClosed(study)) {
      setupWorkflow();
    }
  }, [studyLoading]);

  const FilterMenu = () => (
    <TableFilterMenu
      filterOptions={filterOptions as Array<{ name: string; value: string }>}
      filters={filters}
      filterMatch={filterMatch}
      onUpdateFilters={updateFilters as () => void}
    />
  );

  const OpenWorkflowBtn = () => (
    <Button plain onClick={() => setupWorkflow()} testId="workflow-setup-button">
      Workflow Setup
    </Button>
  );

  const searchPlaceholderText = animalIdsRecord[idToSearch]?.title ?? 'Name';

  const updateFilters = (filters: Array<Record<string, string | number>>, filterMatch: boolean) => {
    setFilterParams(generateFilterParams(filters, filterMatch));
    dispatch({
      type: 'update',
      data: {
        filters,
        filterMatch,
      },
    });
  };

  if (studyLoading || collectionsLoading || subjectsLoading) {
    return (
      <div className="w-100 h-100">
        <Loading />
      </div>
    );
  }
  return (
    <>
      {hasAccess ? (
        <>
          {study && (
            <div className="bg-white relative w-100 flex flex-column vh-100 overflow-auto">
              {study.archived_at ? (
                <>
                  <h2 className="lh-title basier-reg normal f4 f3-l pb3 pa4 pb0-l">Workflow</h2>
                  <div className="pa4">
                    <Banner info className="mw6 mr4 mb4" dismiss={false}>
                      <h3 className="normal lh-title f5 pb1">
                        {`This study was completed on ${DateUtils.renderDateTime(study.archived_at)}`}
                      </h3>
                      <p className="f6 lh-copy pb3">
                        You can't setup a workflow for a study that has been completed. To resume this study please
                        select below.
                      </p>
                      <Button
                        className="plain"
                        onClick={() =>
                          openModal('ARCHIVE_STUDY', {
                            study,
                            onClick: () => confirmUnarchive(study, storeDispatch),
                            closeModal: () => storeDispatch({ type: 'CLOSE_MODAL' }),
                          })
                        }
                      >
                        Resume Study
                      </Button>
                    </Banner>
                  </div>
                </>
              ) : study.canceled_at ? (
                <>
                  <h2 className="lh-title basier-reg normal f4 f3-l pb3 pa4 pb0-l">Workflow</h2>
                  <div className="pa4">
                    <Banner info className="mw6 mr4 mb4" dismiss={false}>
                      <h3 className="normal lh-title f5 pb1">
                        {`This study was cancelled on ${DateUtils.renderDateTime(study.canceled_at)}`}
                      </h3>
                      <p className="f6 lh-copy pb3">
                        You can't setup a workflow for a study that has been cancelled. To resume this study please
                        select below.
                      </p>
                      <Button
                        className="plain"
                        onClick={() =>
                          openModal('CANCEL_STUDY', {
                            study,
                            onClick: () => confirmUncancel(study, storeDispatch),
                            closeModal: () => storeDispatch({ type: 'CLOSE_MODAL' }),
                          })
                        }
                      >
                        Resume Study
                      </Button>
                    </Banner>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-100 pv3 ph4 bb b--moon-gray">
                    <div className="flex justify-between w-100">
                      <div className="flex search-study-container">
                        <SearchFilterBar
                          FilterMenu={FilterMenu}
                          searchPlaceholderText={`Search by Cage or ${searchPlaceholderText}`}
                          searchQuery={searchQuery}
                          handleSearchInput={(searchInput) => {
                            focusSearch();
                            handleSearchQuery({
                              searchQuery: searchInput.toString(),
                              collections: state.collections,
                            });
                          }}
                          autoFocus={true}
                          disabled={_isEmpty(workflow)}
                          searchBarRef={searchBarRef}
                          autoComplete={false}
                          style={{ minWidth: '15rem', maxWidth: '28rem' }}
                        />
                        <div className="flex items-center ph3">
                          <h3 className="f6 black lh-title mr3" style={{ whiteSpace: 'initial', textWrap: 'nowrap' }}>
                            {trunc(study?.name ?? '', 50)}
                          </h3>
                          {_isNotEmpty(unresolvedAlerts(animalAlerts || {})) && (
                            <WorkflowAlerts
                              filters={filters}
                              updateFilters={updateFilters}
                              alertCount={unresolvedAlerts(animalAlerts || {})?.length}
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex">
                        {_isNotEmpty(taskApiIds) && (
                          <div
                            className="br-100 tc w2 h2 dib bg-washed-blue dark-blue bg-light-gray-hover mt1 mr3"
                            onClick={() => {
                              setTaskApiIds([]);
                              removeParamsFromUrl(['taskApiId', 'type'], location);
                              infoToast('Task animals filter has been removed.');
                            }}
                            data-testid="exit-tasks-execution-button"
                          >
                            <Tooltip render="Exit task execution">
                              <span className="flex h-100 w-100 justify-center items-center pointer">
                                <RiCalendarLine />
                              </span>
                            </Tooltip>
                          </div>
                        )}
                        <OpenWorkflowBtn />
                      </div>
                    </div>
                    {_isNotEmpty(filters) && (
                      <Filters filters={filters} filterMatch={filterMatch} updateFilters={updateFilters} />
                    )}
                  </div>
                  <div className="w-100 flex flex-wrap ui__workflow__expand" data-testid="workflow-selection-pane">
                    {_notNil(workflowSetup) && (
                      <AnimalSelectPanel
                        disabled={_isEmpty(workflow)}
                        state={state}
                        workflowSetup={workflowSetup}
                        study={study}
                      />
                    )}
                    {_notNil(selectedAnimal) && _notNil(selectedCage) && (
                      <WorkflowStage
                        study={study}
                        state={state}
                        dispatch={dispatch}
                        openFlash={openFlash}
                        openModal={openModal}
                        closeModal={() => storeDispatch({ type: 'CLOSE_MODAL' })}
                        focusSearch={focusSearch}
                        openSetupWorkflow={setupWorkflow}
                        fetchSubjects={fetchSubjects}
                      />
                    )}
                    {_notNil(workflowSetup) && displayFoodConsumption && (
                      <WorkflowConsumption
                        study={study}
                        collections={collections ?? []}
                        workflowSetup={workflowSetup}
                        focusSearch={focusSearch}
                      />
                    )}
                    <div className="w-60">
                      {_isEmpty(workflow) && !displayFoodConsumption && (
                        <EmptyWorkflow
                          title="Workflow in Benchling In Vivo"
                          text="Quickly record measurements, observations and collect samples for any animal in your study."
                          openWorkflowBtn={OpenWorkflowBtn}
                        />
                      )}
                      {_isNotEmpty(workflow) && _isNil(selectedAnimal) && !displayFoodConsumption && (
                        <EmptyWorkflow
                          title="Search or select an animal"
                          text="Quickly record measurements, observations and collect samples for any animal in your study."
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="w-100 h-100 flex items-center justify-center">
          <NoData
            title="You aren't authorized"
            text="Only team members with Write access have access to use Workflow. The study author can update your permissions to give you access."
            link={`/studies/${studyId}/animals/`}
            btnTxt="Return to animals"
          />
        </div>
      )}
    </>
  );
};

export default Show;
